import { IStore } from 'redux/IStore';
import {
	IServiceGroup,
	IServiceGroups,
	IServiceGroupsAction,
	IServiceGroupsForm,
	IServiceTree,
} from 'models/shop/serviceGroups';
import {
	deleteServiceGroup,
	getServiceGroupsByOffice,
	getServiceGroupsByShop,
	groupServices,
	initialFormValues,
	saveOfficeServiceGroups,
} from './helpers';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { getBundlesForChildren } from '../helpers';
import { actions as notifActions } from 'redux-notifications';
import * as i18n from 'i18next';
import { groupByUnique } from 'containers/Shop/Helpers';

/** Action Types */
export const GET_REQUEST: string = 'serviceGroups/GET_REQUEST';
export const GET_SUCCESS: string = 'serviceGroups/GET_SUCCESS';

export const MANAGE_REQUEST: string = 'serviceGroups/MANAGE_REQUEST';
export const MANAGE_SUCCESS: string = 'serviceGroups/MANAGE_SUCCESS';

export const REQUEST_FAILURE: string = 'serviceGroups/REQUEST_FAILURE';

export const RESET_DATA: string = 'serviceGroups/RESET_DATA';

/** Initial State */
const initialState: IServiceGroups = {
	serviceGroups: [],
};

/** Reducer */
export function serviceGroupsReducer(state = initialState, action: IServiceGroupsAction): IServiceGroups {
	switch (action.type) {
		case MANAGE_REQUEST:
		case GET_REQUEST:
			return { ...state, isFetching: true, error: false };

		case MANAGE_SUCCESS:
		case GET_SUCCESS:
			return {
				...state,
				isFetching: false,
				serviceGroups: action.payload?.serviceGroups || state.serviceGroups || [],
				serviceGroupsById: action.payload?.serviceGroupsById || state.serviceGroupsById,
				fetchedByShop: action.payload?.fetchedByShop,
			};

		case REQUEST_FAILURE:
			return { ...state, isFetching: false, message: action.payload.message, error: true };

		case RESET_DATA:
			return { ...initialState };

		default:
			return state;
	}
}

const gatherServicesDeep = (gss: IServiceTree['children']): IServicesList[] => {
	let ret = [];
	if (!gss?.length) {
		return [];
	}
	for (const gs of gss) {
		if (Array.isArray(gs)) {
			ret = [...ret, ...gs];
		} else {
			ret = [...ret, ...gatherServicesDeep(gs.children)];
		}
	}
	return ret;
};

const gatherServiceGroupsDeep = (gss: IServiceTree['children']): IServiceTree[] => {
	let ret = [];
	if (!gss?.length) {
		return [];
	}
	for (const gs of gss) {
		if (gs && !Array.isArray(gs)) {
			ret = [...ret, gs, ...gatherServiceGroupsDeep(gs.children)];
		}
	}
	return ret;
};
/**
 * Fixes order, applies correct parent Ids
 * @param tree
 */
function normalizeTree(
	tree: IServiceTree['children'],
	parentId: IServiceTree['parentId'] | null,
	level = 0,
): IServiceTree['children'] {
	if (!tree) {
		return null;
	}
	if (level >= 2) {
		// SHOULD BE BookingTemplates
		const bookingTemplates = gatherServicesDeep(tree).map((bt) => ({
			...bt,
			serviceGroupId: parentId,
		}));
		return groupServices(bookingTemplates);
	}
	// ELSE SHOULD BE BookingTemplates
	const ret: IServiceTree[] = [];
	let order = 0;
	for (let i = 0; i < tree.length; i++) {
		const item = tree[i];
		// SHOULD BE ServiceGroups
		if (Array.isArray(item)) {
			const bookingTemplates = item.map((bt) => ({ ...bt, serviceGroupId: null }));
			if (bookingTemplates.length) {
				const uniq = Symbol();
				const children = normalizeTree([bookingTemplates], null, level + 1);
				if (children.length || !level) {
					ret.push({
						children,
						value: bookingTemplates[0].name,
						id: null,
						uniq,
						parentId,
						order: ++order,
					});
				}
			}
		} else {
			const children = normalizeTree(item.children, item.id, level + 1);
			if (children.length || !level) {
				ret.push({
					...item,
					uniq: item.uniq || Symbol(),
					children,
					parentId,
					order: ++order,
				});
			}
		}
	}
	return ret;
}

export function deleteServiceGroups(serviceGroupsIds: string[]) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		dispatch(manageServiceGroupsRequest());
		return Promise.all(serviceGroupsIds.map((sgId) => deleteServiceGroup(getState, sgId))).then(() =>
			dispatch(manageServiceGroupsSuccess()),
		);
	};
}

export function submitServiceGroupsForm(
	form: IServiceGroupsForm,
	shopId: string,
	officeId: string,
	serviceCategoryIds: number[],
) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		dispatch(manageServiceGroupsRequest());
		const initialForm = initialFormValues(getState(), shopId, officeId);
		const serviceIDsToCheck: string[] = [];
		const serviceGroupsToUpdate: IServiceGroup[] = [];
		const serviceGroupsToDelete: string[] = [];

		const findById = <T extends { id?: string }>(id: string, list: T[]): T | undefined =>
			id ? list.find((s) => s.id === id) : undefined;
		// 1. Go through each serviceCategory that is on the shop as we now display all service categories on one page
		serviceCategoryIds.forEach((serviceCategoryId, scIDX) => {
			// Find the indexes of the service category inside the current and initial form
			const newIndex = form.serviceCategoryTree.findIndex((subArray) => {
				return subArray.some((sg) => sg.serviceCategoryId === serviceCategoryId);
			});
			const oldIndex = initialForm.serviceCategoryTree.findIndex((subArray) => {
				return subArray.some((sg) => sg.serviceCategoryId === serviceCategoryId);
			});

			let shouldUpdateParentGroups: boolean = false;

			// If current service category exists
			if (newIndex !== -1) {
				// If current service category order is not the same as initial order
				if (newIndex !== oldIndex) {
					shouldUpdateParentGroups = true;
				}
			}

			const newSectionTree = normalizeTree(form.serviceCategoryTree[newIndex], null, 0) || [];
			const oldSectionTree = initialForm.serviceCategoryTree[oldIndex] || [];

			if (!newSectionTree) {
				console.warn('No section for ' + serviceCategoryId);
			}

			// 1.1 save to be deleted service ID's to serviceIDsToCheck

			const oldServicesIds = gatherServicesDeep(oldSectionTree).map((s) => s.id);
			const newServicesIds = gatherServicesDeep(newSectionTree).map((s) => s.id);
			const tempServicesIdsToCheck = oldServicesIds.filter((s) => !newServicesIds.includes(s));

			serviceIDsToCheck.push(...tempServicesIdsToCheck);

			const oldServiceGroups = gatherServiceGroupsDeep(oldSectionTree);
			const newServiceGroups = gatherServiceGroupsDeep(newSectionTree);

			// 1.2. add service groups to update into serviceGroupsToUpdate array

			for (const newg of newServiceGroups) {
				// Assign different order to parent service groups as they define how service categories are ordered
				const parentServiceGroupOrder = (scIDX + 1) * 1000 + newg.order;
				const complete = {
					id: newg.id,
					value: newg.value,
					parentId: newg.parentId,
					order: !newg?.parentId ? parentServiceGroupOrder : newg.order,
					officeId,
					serviceCategoryId,
					color: newg.color ? newg.color : '#EA3546', // Default to first color if null
				};
				const oldg = findById(newg.id, oldServiceGroups);

				if (oldg) {
					if (
						(shouldUpdateParentGroups && !newg.parentId) ||
						oldg.order !== newg.order ||
						oldg.parentId !== newg.parentId ||
						oldg.value !== newg.value
					) {
						serviceGroupsToUpdate.push(complete);
					}
				}
			}

			// 1.3. add service groups to delete into serviceGroupsToDelete array

			for (const oldg of oldServiceGroups) {
				const newg = findById(oldg.id, newServiceGroups);
				if (!newg && oldg.id && oldg.parentId === null) {
					// Add only parent service groups (BE cascade deletes children SG's and BT's, WATCH OUT FOR CHILDREN BOOKING TEMPLATES THAT ARE INSIDE A DISCOUNT STRATEGY OR BUNDLE)
					serviceGroupsToDelete.push(oldg.id);
				}
			}
		});

		// 2. Check if any to be deleted services are inside a bundle

		return new Promise((resolve, reject) => {
			getBundlesForChildren(getState, serviceIDsToCheck)
				.then((bundles) => {
					// 2.1 If any of the to be deleted services are inside a bundle warn the user otherwise continue with updating

					if (bundles?.length) {
						const bundleNames = `${bundles.map((b) => b.name)}`;
						const message =
							i18n.t('User_ShopCard_ServiceGroups_PartOfNextBundles1') +
							'\n' +
							bundleNames +
							'\n' +
							i18n.t('User_ShopCard_ServiceGroups_PartOfNextBundles2');
						dispatch(notifActions.notifSend({ message: message, kind: 'warning', dismissAfter: 5000 }));
						dispatch(requestFailure('One of the services is part of a bundle'));
						reject('One of the services is part of a bundle');
					} else {
						return saveOfficeServiceGroups(getState, officeId, [], serviceGroupsToUpdate, serviceGroupsToDelete)
							.then(() => {
								dispatch(
									notifActions.notifSend({
										message: i18n.t('User_ShopCard_ServiceGroups_SuccessfullyUpdated'),
										kind: 'success',
										dismissAfter: 5000,
									}),
								);
								dispatch(manageServiceGroupsSuccess());
								resolve(`Successfully updated everything`);
							})
							.catch((err) => {
								dispatch(requestFailure(err));
								reject(err);
							});
					}
				})
				.catch((err) => {
					dispatch(requestFailure(err));
					reject(err);
				});
		});
	};
}

/** Async Action Creator */
export function getOfficeServiceGroups(officeId: string, groupById?: boolean) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (officeId) {
			dispatch(getServiceGroupsRequest());
			return getServiceGroupsByOffice(getState, officeId)
				.then((templates) => dispatch(getServiceGroupsSuccess(templates, false, groupById)))
				.catch((err) => dispatch(requestFailure(err)));
		} else {
			console.error('Office ID missing');
			dispatch(resetServiceGroupsData());
			return Promise.resolve(0);
		}
	};
}

/** Async Action Creator */
export function getShopServiceGroups(shopId: string, groupById?: boolean) {
	return (dispatch, getState: () => IStore): Promise<any> => {
		if (shopId) {
			dispatch(getServiceGroupsRequest());
			return getServiceGroupsByShop(getState, shopId)
				.then((templates) => dispatch(getServiceGroupsSuccess(templates, true, groupById)))
				.catch((err) => dispatch(requestFailure(err)));
		} else {
			dispatch(resetServiceGroupsData());
			return Promise.resolve(0);
		}
	};
}

// /** Action Creator */
export function manageServiceGroupsRequest(): IServiceGroupsAction {
	return {
		type: MANAGE_REQUEST,
	};
}
export function getServiceGroupsRequest(): IServiceGroupsAction {
	return {
		type: GET_REQUEST,
		payload: {
			serviceGroups: [],
		},
	};
}

// /** Action Creator */
export function manageServiceGroupsSuccess(): IServiceGroupsAction {
	return {
		type: MANAGE_SUCCESS,
	};
}
export function getServiceGroupsSuccess(
	serviceGroups: IServiceGroup[],
	fetchedByShop?: boolean,
	groupById?: boolean,
): IServiceGroupsAction {
	const payload: IServiceGroupsAction['payload'] = {
		serviceGroups,
		fetchedByShop,
	};

	if (groupById) {
		payload['serviceGroupsById'] = groupByUnique(serviceGroups || [], 'id');
	}

	return {
		type: GET_SUCCESS,
		payload,
	};
}

export function requestFailure(message: any): IServiceGroupsAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

// /** Action Creator */
export function resetServiceGroupsData(): IServiceGroupsAction {
	return {
		type: RESET_DATA,
	};
}
